import { useEffect, useState } from "react";
import LazyImage from "@Components/Image";
import Portal from "@Components/Portal";
import Modal from "@Components/Modal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import CloseIcon from "@Icons/CloseIcon";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import useUcasModalTrigger from "./useUcasModalTrigger";
import styles from "./UcasAnnouncementModal.module.scss";

// Hosted on static-assets S3 (images/amber-fest/), not bundled from ./assets.
const HERO_IMG = `${config.IMAGE_STATIC_URL}/images/amber-fest/ucas-hero.jpg`;
const SPARKLE_IMG = `${config.IMAGE_STATIC_URL}/images/amber-fest/ucas-sparkle.png`;

const UcasAnnouncementModal = () => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const { shouldShow, markSeen } = useUcasModalTrigger();
  const [step, setStep] = useState(-1);

  // The sheet itself animates up over ~300ms, so the inner reveal starts after
  // that: hero settles first, then each line springs in.
  useEffect(() => {
    if (!shouldShow) return undefined;

    const timers = [320, 540, 700, 860].map((delay, index) =>
      setTimeout(() => setStep(index), delay),
    );

    return () => timers.forEach(clearTimeout);
  }, [shouldShow]);

  if (!shouldShow) return null;

  // Mobile gets an explicit close button over the hero; desktop keeps backdrop
  // click / ESC, and both can dismiss via the CTA.
  const content = (
    <div className={styles.content}>
      {/* Decorative hero: a background-image keeps the crop entirely in CSS, since
          LazyImage's required width/height props render as inline attributes that
          would override the stylesheet. */}
      <div
        className={formatClassNames(styles.hero, step >= 0 && styles.heroIn)}
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />

      {!isDesktop && (
        <button
          type="button"
          className={styles.closeBtn}
          onClick={markSeen}
          aria-label={t("mischlaneous.dismiss_announcement")}
        >
          <CloseIcon size={16} strokeWidth="2" stroke="#374151" />
        </button>
      )}

      <div className={styles.body}>
        <div className={styles.copy}>
          <p className={formatClassNames(styles.title, step >= 1 && styles.lineIn)}>
            {t("mischlaneous.ucas_is_in_full_swing")}
          </p>
          <p className={formatClassNames(styles.subtitle, step >= 2 && styles.lineIn)}>
            <span className={styles.highlight}>80%</span>{" "}
            {t("mischlaneous.of_rooms_are_already_booked")}
          </p>
        </div>

        <div className={formatClassNames(styles.ctaWrapper, step >= 3 && styles.lineIn)}>
          <button type="button" className={styles.ctaBtn} onClick={markSeen}>
            {t("mischlaneous.book_your_spot_today")}
          </button>
          <span className={styles.sparkle} aria-hidden="true">
            <LazyImage src={SPARKLE_IMG} alt="" width="16px" height="26px" isNotLazy />
          </span>
        </div>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Portal rootId="forPortal">
        <Modal
          hideModal={markSeen}
          size="fitContent"
          animation="opacity"
          align="center"
          height="fit-content"
          hideCloseButton
          hideCloseBtn
          className={styles.desktopModal}
          coverClassName={styles.desktopOverlay}
        >
          {content}
        </Modal>
      </Portal>
    );
  }

  return (
    <Portal rootId="forPortal">
      <BottomSheetOverlay
        hideBottomSheetOverlay={markSeen}
        size="fitContent"
        forceHideLine
        noBlurBackground
        name="UcasAnnouncementModal"
        innerClassName={styles.sheetInner}
        backdropClassName={styles.sheetOverlay}
      >
        {content}
      </BottomSheetOverlay>
    </Portal>
  );
};

export default wrapperHOC(UcasAnnouncementModal, {
  componentName: "UcasAnnouncementModal",
  showForChina: false,
  showForWhiteLable: false,
  showForApp: false,
});
